/*
 * The reply on its way, as the thread shows it once its hold is over: what the rep sent, kept in
 * this browser's query cache from the press of Send, drawn as our newest message with "Sending…" and
 * then "Sent" until Bison's own copy of it reaches the thread and takes its place (Dawid's approval,
 * 2026-11-03: fold the box when the Undo window ends and show the outgoing message at once, as Gmail
 * does). Only this browser has the words: after a reload nothing is drawn, and the reply box's bar
 * alone says the reply is going. The send's state is never copied here; it is read from the reply
 * box's own status (`sendStatusQuery`), which that box polls.
 */
import { type QueryClient, skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type {
  InboxMessage,
  InboxSendRequest,
  InboxSendStatus,
  InboxThread,
} from "../../src/inbox-v2/contract.ts";
import { localCopyMatch } from "../../src/rep-reply.ts";
import { inboxKey } from "../queries.ts";
import { sendStatusQuery } from "./use-reply-send.ts";

type Send = NonNullable<InboxSendStatus["send"]>;

/** What the rep sent, as Send was pressed. */
type OutgoingReply = {
  /** The send's operation id, which the outbox row and its status take: drawn only for that send. */
  readonly id: string;
  /** The message it answers: the turn for `localCopyMatch`, and the status read's key. */
  readonly messageId: string;
  /** Replying to a message other than the lead's newest, so the status names it. */
  readonly selected: boolean;
  /** When Send was pressed: Bison's copy is dated no earlier than five minutes before it. */
  readonly at: string;
  /** Every message the thread held then; none of them can be this reply's copy. */
  readonly before: readonly string[];
  /** The mailbox it goes from, as the thread's own messages name their sender. */
  readonly from: string | null;
  readonly to: readonly string[];
  readonly cc: readonly string[];
  /** The words as text: the sent email's markup comes with Bison's copy. */
  readonly plainText: string;
};

/** Where the thread's reply in flight is drawn from, one per conversation. */
const outgoingKey = (workspaceId: string, planId: string) =>
  [...inboxKey(workspaceId), "outgoing", planId] as const;

/**
 * Kept while no thread shows it, for a rep who leaves the conversation and comes back: well past the
 * half minute a send takes, so a copy late from Bison still finds the card in its place.
 */
const KEPT_MS = 30 * 60_000;

/** Forgets the kept reply if it is still this send's; a newer press has replaced it otherwise. */
function forgetOutgoing(client: QueryClient, workspaceId: string, planId: string, id: string) {
  client.setQueryData<OutgoingReply | null>(outgoingKey(workspaceId, planId), (kept) =>
    kept?.id === id ? null : kept,
  );
}

/** Bison's own copy of the reply, now in the thread: the established rule, R6 (`rep-reply.ts`). */
function bisonCopy(reply: OutgoingReply, messages: readonly InboxMessage[]): boolean {
  const fresh = messages.filter(
    (message) => message.id === reply.messageId || !reply.before.includes(message.id),
  );
  return localCopyMatch({ messages: fresh }, reply.messageId, reply.at) !== null;
}

/**
 * What the thread does with a kept reply: draw it going or gone, forget it (undone, refused, or its
 * copy arrived), or wait (still held, an outcome nobody knows, or a send the status does not name).
 */
function outgoingView(
  reply: OutgoingReply | null | undefined,
  send: Send | null | undefined,
  messages: readonly InboxMessage[],
): "sending" | "sent" | "forget" | "none" {
  if (!reply) return "none";
  if (bisonCopy(reply, messages)) return "forget";
  if (send?.id !== reply.id) return "none";
  if (send.state === "claimed") return "sending";
  if (send.state === "sent") return "sent";
  return send.state === "cancelled" || send.state === "refused" ? "forget" : "none";
}

/** The thread's reply in flight, while its hold is over and Bison's copy is not yet in the thread. */
export function useOutgoingReply(
  workspaceId: string,
  thread: InboxThread,
): { reply: OutgoingReply; state: "sending" | "sent" } | null {
  const client = useQueryClient();
  const planId = thread.conversation.id;
  const { data: reply } = useQuery<OutgoingReply | null>({
    queryKey: outgoingKey(workspaceId, planId),
    queryFn: skipToken,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: KEPT_MS,
  });
  // Read, never fetched or polled from here: the reply box that sent it polls the same key.
  const { data: status } = useQuery({
    ...sendStatusQuery(
      workspaceId,
      planId,
      reply?.messageId ?? thread.conversation.leadMessageId,
      "reply",
      reply?.selected ?? false,
    ),
    enabled: false,
  });
  const view = outgoingView(reply, status?.send, thread.messages);
  const forget = view === "forget" ? reply?.id : undefined;
  useEffect(() => {
    if (forget) forgetOutgoing(client, workspaceId, planId, forget);
  }, [client, workspaceId, planId, forget]);
  return reply && (view === "sending" || view === "sent") ? { reply, state: view } : null;
}

/**
 * Keeps what the rep sends from the moment they press Send, and forgets it again when that press
 * queued nothing: refused, locked, or an outcome nobody knows, which never draws a card. `pressed`
 * names the last send pressed in this box, so only a send the rep just made folds it.
 */
export function useOutgoingRecorder(workspaceId: string, thread: InboxThread) {
  const client = useQueryClient();
  const planId = thread.conversation.id;
  const pressed = useRef<string | null>(null);
  const record = (request: InboxSendRequest, selected: boolean) => {
    pressed.current = request.operationId;
    const kept: OutgoingReply = {
      id: request.operationId,
      messageId: request.messageId,
      selected,
      at: new Date().toISOString(),
      before: thread.messages.map((message) => message.id),
      from: thread.mailbox,
      to: request.reply?.recipients?.to ?? [],
      cc: request.reply?.recipients?.cc ?? [],
      plainText: request.plainText,
    };
    client.setQueryData<OutgoingReply | null>(outgoingKey(workspaceId, planId), kept);
  };
  const settle = (request: InboxSendRequest) => {
    const status = client.getQueryData(
      sendStatusQuery(workspaceId, planId, request.messageId).queryKey,
    );
    if (status?.send?.id !== request.operationId)
      forgetOutgoing(client, workspaceId, planId, request.operationId);
  };
  return { pressed, record, settle };
}
